import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { ROLE_LABELS } from "../lib/rbac";
import { LogOut, User as UserIcon } from "lucide-react";

function initials(name = "") {
    return name.split(" ").filter(Boolean).map((s) => s[0]?.toUpperCase()).slice(0, 2).join("");
}

/** Avatar + dropdown shown in every authenticated shell header. */
export default function UserMenu() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        if (!open) return undefined;
        const onClick = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", onClick);
        return () => document.removeEventListener("mousedown", onClick);
    }, [open]);

    const handleLogout = async () => {
        setOpen(false);
        await logout();
        navigate("/login", { replace: true });
    };

    return (
        <div className="relative" ref={ref}>
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                aria-haspopup="menu"
                aria-expanded={open}
                className="h-10 w-10 rounded-full flex items-center justify-center text-white font-semibold asl-navy-bg hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-[color:var(--asl-crimson)]"
                data-testid="header-avatar-btn"
            >
                {initials(user?.name)}
            </button>
            {open && (
                <div
                    className="absolute right-0 top-12 bg-white shadow-lg border rounded-md py-1 w-56 z-50"
                    style={{ borderColor: "var(--asl-border)" }}
                    role="menu"
                    data-testid="user-menu-popover"
                >
                    <div className="px-3 py-2 border-b" style={{ borderColor: "var(--asl-border)" }}>
                        <div className="text-sm font-semibold asl-navy-text" data-testid="user-menu-name">
                            {user?.name}
                        </div>
                        <div className="text-[11px] text-[color:var(--asl-muted)]" data-testid="user-menu-role">
                            {ROLE_LABELS[user?.role] || user?.role}
                        </div>
                        <div className="text-[11px] text-[color:var(--asl-muted)] truncate" data-testid="user-menu-email">
                            {user?.email}
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={() => { setOpen(false); navigate("/account"); }}
                        className="w-full text-left px-3 py-2 text-sm flex items-center gap-2 hover:bg-gray-50 asl-navy-text"
                        data-testid="user-menu-account"
                    >
                        <UserIcon className="h-4 w-4" /> My Account
                    </button>
                    <button
                        type="button"
                        onClick={handleLogout}
                        className="w-full text-left px-3 py-2 text-sm flex items-center gap-2 hover:bg-rose-50 text-rose-700"
                        data-testid="user-menu-logout"
                    >
                        <LogOut className="h-4 w-4" /> Sign out
                    </button>
                </div>
            )}
        </div>
    );
}
